import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "../ui/sheet";
import { NAV_LINKS, WHATSAPP_LINK, IMG } from "../../data/site";
import { useLang } from "../../i18n/LanguageContext";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -80, duration: 1.4 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const { t, lang, toggle } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream/95 shadow-[0_1px_0_rgba(197,160,89,0.35)] backdrop-blur-xl py-2"
          : "bg-cream/70 backdrop-blur-md py-4"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToId("home")}
          className="flex items-center shrink-0"
          aria-label="Tropical Vanilla home"
        >
          <img
            src={IMG.logo}
            alt="Tropical Vanilla Co."
            className={`transition-all duration-500 ${scrolled ? "h-8 md:h-9" : "h-9 md:h-11"} w-auto object-contain`}
          />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollToId(l.id)}
              className="overline text-ink/70 hover:text-gold transition-colors duration-300"
            >
              {t.nav[l.id]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="lang-toggle"
            className="hidden sm:flex items-center rounded-full border border-gold/40 overflow-hidden"
          >
            <button
              data-testid="lang-en"
              onClick={() => lang !== "en" && toggle()}
              className={`px-3 py-1.5 text-xs font-semibold tracking-wider transition-colors duration-300 ${
                lang === "en" ? "bg-gold text-white" : "text-ink/70 hover:text-gold"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-id"
              onClick={() => lang !== "id" && toggle()}
              className={`px-3 py-1.5 text-xs font-semibold tracking-wider transition-colors duration-300 ${
                lang === "id" ? "bg-gold text-white" : "text-ink/70 hover:text-gold"
              }`}
            >
              ID
            </button>
          </div>

          <a
            data-testid="nav-quote-button"
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center bg-gold hover:bg-golddark text-white overline px-6 py-3 transition-colors duration-300"
          >
            {t.common.getQuote}
          </a>

          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="lg:hidden p-2 text-ink"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream border-l border-gold/30 w-[280px]">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="mt-10 flex flex-col gap-1">
                <div
                  data-testid="mobile-lang-toggle"
                  className="mb-4 flex w-max items-center rounded-full border border-gold/40 overflow-hidden"
                >
                  <button
                    data-testid="mobile-lang-en"
                    onClick={() => lang !== "en" && toggle()}
                    className={`px-4 py-1.5 text-xs font-semibold tracking-wider ${
                      lang === "en" ? "bg-gold text-white" : "text-ink/70"
                    }`}
                  >
                    EN
                  </button>
                  <button
                    data-testid="mobile-lang-id"
                    onClick={() => lang !== "id" && toggle()}
                    className={`px-4 py-1.5 text-xs font-semibold tracking-wider ${
                      lang === "id" ? "bg-gold text-white" : "text-ink/70"
                    }`}
                  >
                    ID
                  </button>
                </div>
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.id}>
                    <button
                      data-testid={`mobile-nav-link-${l.id}`}
                      onClick={() => scrollToId(l.id)}
                      className="text-left font-serif text-2xl text-ink hover:text-gold py-2 transition-colors"
                    >
                      {t.nav[l.id]}
                    </button>
                  </SheetClose>
                ))}
                <a
                  data-testid="mobile-quote-button"
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center justify-center bg-gold hover:bg-golddark text-white overline px-6 py-4 transition-colors"
                >
                  {t.common.getQuote}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
